import { useState, useEffect, useRef, ElementRef } from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { CardMedia } from '@mui/material';

import type { ChallengeImages } from '@source/graphql/autogenerate/schemas';
import * as S from '../styles';
import CarouselDots from '../CarouselDots';

import Lottie from 'react-lottie';
import * as animationData from './claps.json';
import SuspenseLoader from '@source/components/SuspenseLoader';
import Image from 'next/legacy/image';

type Props = {
  images: ChallengeImages[];
  imageAvatar: string;
  handleOpenModal: (index: number) => void;
  deviceType: string;
  displayUserClaps: boolean;
};

const ArrowStyle = {
  color: '#161C25',
  fontSize: '1.2rem'
};

const ButtonGroup = ({ next, previous, ...rest }) => {
  const {
    carouselState: { currentSlide }
  } = rest;
  return (
    <S.ButtonGroupWrapper>
      <S.ArrowButton
        variant="outlined"
        className={currentSlide === 0 ? 'disable' : ''}
        onClick={() => previous()}
      >
        <ArrowBackIosNewIcon style={{ ...ArrowStyle }} />
      </S.ArrowButton>

      <S.ArrowButton variant="outlined" onClick={() => next()}>
        <ArrowForwardIosIcon style={{ ...ArrowStyle }} />
      </S.ArrowButton>
    </S.ButtonGroupWrapper>
  );
};

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice'
  }
};

const CarouselCardRender: React.FC<Props> = ({
  images,
  imageAvatar,
  handleOpenModal,
  displayUserClaps,
  deviceType
}): JSX.Element => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [displayClapLottie, setDisplayClapLottie] = useState<boolean>(false);

  const carouselWrapperRef = useRef<ElementRef<'div'>>(null);

  // Width da imagem do carrossel para renderização dinâmica da altura
  const carouselImageWidth = carouselWrapperRef?.current?.clientWidth ?? 250;

  const displayLottie = () => {
    setTimeout(() => setDisplayClapLottie(false), 1200);
  };

  useEffect(() => {
    if (!displayUserClaps) {
      return;
    }
    setDisplayClapLottie(true);
    displayLottie();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayUserClaps]);

  return (
    <SuspenseLoader loading={!images} fitContainer style={{ margin: '2rem 0' }}>
      <S.CarouselWrapper ref={carouselWrapperRef}>
        <Carousel
          afterChange={(previousSlide, { currentSlide }) => {
            if (currentSlide > previousSlide) {
              setCurrentImageIndex(previous => previous + 1);
              return;
            }
            if (previousSlide > currentSlide) {
              setCurrentImageIndex(previous => previous - 1);
              return;
            }
          }}
          ssr
          deviceType={deviceType}
          slidesToSlide={1}
          draggable
          swipeable
          arrows={false}
          focusOnSelect
          infinite={false}
          shouldResetAutoplay={false}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          customButtonGroup={images?.length > 1 && <ButtonGroup />}
          renderButtonGroupOutside
          responsive={{
            desktop: {
              breakpoint: {
                max: 3000,
                min: 1024
              },
              items: 1
            },
            tablet: {
              breakpoint: {
                max: 1024,
                min: 464
              },
              items: 1
            },
            mobile: {
              breakpoint: {
                max: 464,
                min: 0
              },
              items: 1
            }
          }}
        >
          {images.map((image, index) => {
            return (
              <S.ImageButtonWrapper
                key={image.id}
                onClick={() => handleOpenModal(index)}
              >
                <Image
                  src={`/${image.link}?wdt=411&hgt=493.2`}
                  loader={({ src }) =>
                    `${process.env.NEXT_PUBLIC_CDN_URL}${src}`
                  }
                  alt="Imagem do desafio"
                  width={carouselImageWidth}
                  height={Number(carouselImageWidth * 1.2)}
                  blurDataURL={`${process.env.NEXT_PUBLIC_CDN_URL}/${image.link}?wdt=41.7&hgt=50`}
                  placeholder="blur"
                  priority
                />

                {displayClapLottie && (
                  <>
                    <S.LottieBox>
                      <Lottie options={defaultOptions} />
                    </S.LottieBox>

                    <S.LottieBackground></S.LottieBackground>
                  </>
                )}

                <CardMedia
                  component="img"
                  image={`${process.env.NEXT_PUBLIC_CDN_URL}/${imageAvatar}?wdt=72&hgt=72`}
                  alt="Imagem do avatar"
                  sx={{
                    width: '7rem',
                    height: '7rem',
                    position: 'absolute',
                    bottom: '2.4rem',
                    left: '2.4rem'
                  }}
                />
              </S.ImageButtonWrapper>
            );
          })}
        </Carousel>
      </S.CarouselWrapper>

      <CarouselDots slide={currentImageIndex} images={images} />
    </SuspenseLoader>
  );
};

export default CarouselCardRender;
